'use client';

import * as React from 'react';
import {
  Tabs as ShadcnTabs,
  TabsList as ShadcnTabsList,
  TabsTrigger as ShadcnTabsTrigger,
  TabsContent as ShadcnTabsContent,
} from '@/hen-ui/shadcn/tabs';
import { TabButton, tabButtonVariants } from '@/hen-ui/atomics/TabButton';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  useModalOpenPayload,
  type ModalOpenPayload,
} from '@/hen-ui/composites/Modal/ModalHost';
import { cn } from '@/hen-ui/utils';
import type {
  TabsGap,
  TabsListAlign,
  TabsLook,
  TabsPanelHeight,
  TabsTrackColor,
  TabsUnselectedStyle,
} from './tabs.options';

/**
 * Visual look of the tab triggers.
 *
 * - `underline` — selected tab is marked with a line under the label.
 * - `accent` — same underlined look; line uses primary-700.
 * - `button` — hen-ui TabButton pills (outline or filled rest, filled black
 *   when selected), colored by the `--cds-tab-button-*` tokens.
 * - `track` — equal-width tabs in a rounded outline; a filled pill slides to the selected tab.
 * - `default` — legacy alias of `underline` (homepage / shortcode configs).
 */
export type TabsVariant = TabsLook | 'default';

export const resolveTabsLook = (variant: TabsVariant | undefined): TabsLook =>
  variant === 'accent' || variant === 'button' || variant === 'track'
    ? variant
    : 'underline';

const TRACK_COLOR_CLASSES: Readonly<
  Record<TabsTrackColor, { track: string; label: string }>
> = Object.freeze({
  default: {
    track: 'border-neutral-900 before:bg-neutral-900',
    label: 'data-[state=active]:text-white',
  },
  primary: {
    track: 'border-primary-700 before:bg-primary-700',
    label: 'data-[state=active]:text-primary-on-fill',
  },
  secondary: {
    track: 'border-secondary-700 before:bg-secondary-700',
    label: 'data-[state=active]:text-secondary-on-fill',
  },
  tertiary: {
    track: 'border-tertiary-700 before:bg-tertiary-700',
    label: 'data-[state=active]:text-tertiary-on-fill',
  },
  accent: {
    track: 'border-accent-700 before:bg-accent-700',
    label: 'data-[state=active]:text-accent-on-fill',
  },
});

const resolveTrackColorClasses = (color: string | undefined) =>
  color !== undefined &&
  Object.prototype.hasOwnProperty.call(TRACK_COLOR_CLASSES, color)
    ? TRACK_COLOR_CLASSES[color as TabsTrackColor]
    : TRACK_COLOR_CLASSES.default;

const TABS_GAP_CLASSES: Readonly<Record<TabsGap, string>> = Object.freeze({
  sm: 'gap-sm',
  lg: 'gap-lg',
});

const resolveTabsGapClass = (gap: string | undefined) =>
  gap !== undefined &&
  Object.prototype.hasOwnProperty.call(TABS_GAP_CLASSES, gap)
    ? TABS_GAP_CLASSES[gap as TabsGap]
    : undefined;

const resolveTabsPanelHeight = (
  panelHeight: string | undefined,
): TabsPanelHeight =>
  panelHeight === 'selected' || panelHeight === 'fill'
    ? panelHeight
    : 'tallest';

const tabsPanelContainerClass = (panelHeight: TabsPanelHeight) => {
  if (panelHeight === 'tallest') {
    return 'grid w-full min-w-0 [&>*]:col-start-1 [&>*]:row-start-1';
  }
  if (panelHeight === 'fill') {
    return 'relative w-full min-w-0 flex-1 min-h-0';
  }
  return 'relative w-full min-w-0';
};

const tabsPanelContentClass = (
  panelHeight: TabsPanelHeight,
  isActive: boolean,
) => {
  if (panelHeight === 'tallest') return undefined;
  if (!isActive) return 'absolute inset-0 overflow-hidden';
  return panelHeight === 'fill' ? 'h-full' : undefined;
};

export interface TabRecord {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
  content?: React.ReactNode;
}

export interface TabItemProps {
  /** Words shown on the tab trigger. */
  label: string;
  /** Panel body (children slot). */
  children?: React.ReactNode;
  /** Stable id for default-open / deep-linking. Derived from `label` when omitted. */
  value?: string;
  disabled?: boolean;
}

type TabsRootProps = Omit<
  React.ComponentPropsWithoutRef<typeof ShadcnTabs>,
  'children' | 'defaultValue' | 'orientation' | 'color'
> & {
  /** Ignored by the `track` look, which always runs in a row. */
  orientation?: 'horizontal' | 'vertical';
  defaultValue?: string;
  /**
   * How the tabs look. `underline` (and legacy `default`) mark the selected
   * tab with a line; `accent` uses the brand color for that line; `button`
   * renders hen-ui TabButton pills; `track` fills the row with equal tabs inside a
   * rounded outline.
   */
  variant?: TabsVariant;
  /**
   * Horizontal tab row alignment. `center` (default) or `start`, which follows
   * the writing direction. No effect on vertical tabs or the `track` look.
   */
  listAlign?: TabsListAlign;
  /**
   * Unselected tab style for the `button` look. `outline` (default) or
   * `filled` with a neutral gray. Ignored by other looks.
   */
  unselectedStyle?: TabsUnselectedStyle;
  /**
   * Selected pill and outline color for the `track` look. `default` is black.
   * Ignored by other looks.
   */
  color?: TabsTrackColor;
  /**
   * Space between the tab list and the panel, for every look and orientation.
   * `sm` or `lg`; omitted keeps the default spacing.
   */
  gap?: TabsGap;
  /**
   * How tall the panel area is. `tallest` (default) matches the longest panel.
   * `selected` matches the open panel. `fill` stretches the tabs to fill their
   * parent and the open panel fills the space under the tab list.
   */
  panelHeight?: TabsPanelHeight;
};

type TabsItemsProps = TabsRootProps & {
  tabs: TabRecord[];
  children?: never;
};

type TabsCompoundProps = TabsRootProps & {
  /** `TabItem` children. */
  children: React.ReactNode;
  tabs?: never;
};

export type TabsProps = TabsItemsProps | TabsCompoundProps;

/**
 * A single tab for use as a child of `Tabs`. Parent `Tabs` collects
 * `label` / `value` / `children` and builds one tab list — this component
 * is a data carrier and does not paint its own list.
 */
export const TabItem: React.FC<TabItemProps> = () => null;

TabItem.displayName = 'TabItem';

const isTabItemElement = (
  child: React.ReactNode,
): child is React.ReactElement<TabItemProps> => {
  if (!React.isValidElement(child)) return false;
  if (child.type === TabItem) return true;
  const type = child.type as { displayName?: string };
  return type.displayName === 'TabItem';
};

const isFragmentElement = (
  child: React.ReactNode,
): child is React.ReactElement<{ children?: React.ReactNode }> =>
  React.isValidElement(child) && child.type === React.Fragment;

/** One Fragment only — not a depth walk. `Children.toArray` does the rest. */
const unwrapSingleFragment = (children: React.ReactNode): React.ReactNode =>
  isFragmentElement(children) ? children.props.children : children;

const slugFromLabel = (label: React.ReactNode, index: number): string => {
  if (typeof label === 'string' && label.trim() !== '') {
    return label.trim().toLowerCase().replace(/\s+/g, '-');
  }
  return `tab-${index}`;
};

const uniqueTabValue = (base: string, used: Set<string>): string => {
  if (!used.has(base)) {
    used.add(base);
    return base;
  }
  let n = 2;
  while (used.has(`${base}-${n}`)) n += 1;
  const next = `${base}-${n}`;
  used.add(next);
  return next;
};

/**
 * Direct `TabItem` children only. Unwrap one Fragment so `<>…</>` authors
 * work; custom slot hosts are the catalog runtime's job.
 */
const collectTabItemElements = (
  children: React.ReactNode,
): React.ReactElement<TabItemProps>[] =>
  React.Children.toArray(unwrapSingleFragment(children)).filter(
    isTabItemElement,
  );

const recordsFromTabItemProps = (items: TabItemProps[]): TabRecord[] => {
  const used = new Set<string>();
  const values: string[] = new Array(items.length);

  // Explicit `value` wins: claim those first so a later slug cannot steal them.
  items.forEach((item, i) => {
    const explicit = item.value?.trim();
    if (!explicit) return;
    values[i] = uniqueTabValue(explicit, used);
  });
  items.forEach((item, i) => {
    if (item.value?.trim()) return;
    values[i] = uniqueTabValue(slugFromLabel(item.label, i), used);
  });

  return items.map((item, i) => ({
    value: values[i],
    label: item.label,
    disabled: item.disabled,
    content: item.children,
  }));
};

const collectTabItems = (children: React.ReactNode): TabRecord[] =>
  recordsFromTabItemProps(
    collectTabItemElements(children).map((child) => child.props),
  );

/**
 * Tabs component following HEN Design System standards.
 *
 * A thin wrapper around shadcn/ui tabs (Radix UI Tabs primitive) with HEN-specific
 * variants and design tokens. All styling uses tokens from tailwind.config.ts.
 *
 * Supports a programmatic `tabs` array and compound `TabItem` children (DSL
 * drop zone). Use one or the other, not both.
 *
 * @example
 * // Basic usage - horizontal orientation by default
  <Tabs
    tabs={[
      {
        value: 'tab1',
        label: 'Tab 1',
        content: <div>Content for Tab 1</div>
      },
      {
        value: 'tab2',
        label: 'Tab 2',
        content: <div>Content for Tab 2</div>
      }
    ]}
  />

 *
 * @example
 * // Compound TabItem children (authorable panels)
  <Tabs variant="button" defaultValue="about">
    <TabItem value="about" label="About">
      <div>About panel</div>
    </TabItem>
    <TabItem value="reviews" label="Reviews">
      <div>Reviews panel</div>
    </TabItem>
  </Tabs>
 */

type TabsRenderableProps = TabsRootProps & {
  tabs?: TabRecord[];
  children?: React.ReactNode;
  variant?: TabsVariant;
};

/**
 * Programmatic `tabs[]` only when the array actually has rows. Leftover
 * `tabs={[]}` (Puck default / Outline TABS section) and leftover non-empty
 * story/catalog defaults must not steal compound `TabItem` children when
 * those children are present — canvas authors Tabs as TabItem children.
 */
const hasNonEmptyTabsArray = (
  props: TabsRenderableProps,
): props is TabsRenderableProps & { tabs: TabRecord[] } =>
  Array.isArray(props.tabs) && props.tabs.length > 0;

/**
 * Equal-width tabs let the selected pill be placed with CSS alone: the pill is
 * the list's `before:` layer, sized from `--tabs-count` and offset by
 * `--tabs-index`. The calc terms must match the list's `p-1` inset and
 * `gap-xxs` gap.
 */
const TRACK_LIST_CLASS_NAME = [
  'relative flex w-full p-1 gap-xxs rounded-full border bg-white overflow-hidden',
  'group-data-[orientation=horizontal]/tabs:h-12',
  '[--tabs-pill-width:calc((100%_-_0.5rem_-_(var(--tabs-count)_-_1)_*_var(--space-xxs,8px))_/_var(--tabs-count))]',
  'before:absolute before:inset-y-1 before:w-[var(--tabs-pill-width)] before:rounded-full',
  'before:[inset-inline-start:calc(0.25rem_+_var(--tabs-index)_*_(var(--tabs-pill-width)_+_var(--space-xxs,8px)))]',
  'before:transition-[inset-inline-start] before:duration-slower before:ease-emphasized motion-reduce:before:transition-none',
].join(' ');

const TrackTabTriggers = ({
  records,
  color,
  selectedValue,
  triggerIdentity,
}: {
  records: TabRecord[];
  color: TabsTrackColor | undefined;
  selectedValue: string | undefined;
  triggerIdentity: string;
}) => {
  const colorClasses = resolveTrackColorClasses(color);
  const selectedIndex = records.findIndex((tab) => tab.value === selectedValue);
  const pillStyle = {
    '--tabs-count': String(records.length),
    '--tabs-index': String(Math.max(selectedIndex, 0)),
  } as React.CSSProperties;

  return (
    <ShadcnTabsList
      className={cn(
        TRACK_LIST_CLASS_NAME,
        colorClasses.track,
        selectedIndex < 0 && 'before:hidden',
      )}
      style={pillStyle}
    >
      {records.map((tab) => (
        <ShadcnTabsTrigger
          key={tab.value}
          value={tab.value}
          disabled={tab.disabled}
          className={cn(
            'h-full min-w-0 basis-0 rounded-full after:hidden',
            'transition-colors duration-slower motion-reduce:transition-none',
            'focus-visible:rounded-full focus-visible:ring-neutral-700 focus-visible:outline-neutral-700',
            colorClasses.label,
          )}
          data-hen-id={triggerIdentity}
        >
          {/* hen-ui-allow-raw-html -- clips a long tab name with an ellipsis while the tab keeps its own label type */}
          <span className='truncate'>{tab.label}</span>
        </ShadcnTabsTrigger>
      ))}
    </ShadcnTabsList>
  );
};

const TabTriggers = ({
  records,
  look,
  unselectedStyle,
  triggerIdentity,
}: {
  records: TabRecord[];
  look: Exclude<TabsLook, 'track'>;
  unselectedStyle: TabsUnselectedStyle;
  triggerIdentity: string;
}) => (
  <ShadcnTabsList
    className={cn(
      // Keep vertical list at label+gap intrinsic width so nowrap text
      // cannot shrink under the absolute end indicator.
      'group-data-[orientation=vertical]/tabs:shrink-0 group-data-[orientation=vertical]/tabs:max-w-none group-data-[orientation=vertical]/tabs:overflow-x-visible',
      look === 'button' &&
        'gap-sm group-data-[orientation=horizontal]/tabs:h-auto',
    )}
  >
    {records.map((tab) => {
      if (look !== 'button') {
        return (
          <ShadcnTabsTrigger
            key={tab.value}
            value={tab.value}
            disabled={tab.disabled}
            className={cn(
              // pr-4 (~16px) clears after:right-0; list shrink-0/max-w-none
              // above keeps that padding real so nowrap labels cannot run under
              // the absolute indicator.
              'group-data-[orientation=vertical]/tabs:pr-4 group-data-[orientation=vertical]/tabs:after:right-0',
              look === 'accent' && 'data-[state=active]:after:bg-primary-700',
            )}
            data-hen-id={triggerIdentity}
          >
            {tab.label}
          </ShadcnTabsTrigger>
        );
      }

      // Slot concatenates the trigger and TabButton classes rather than
      // merging them, so the trigger takes the TabButton classes too to
      // override its own underline-tab chrome.
      return (
        <ShadcnTabsTrigger
          key={tab.value}
          value={tab.value}
          disabled={tab.disabled}
          asChild
          className={cn(
            'after:hidden',
            tabButtonVariants({ appearance: unselectedStyle }),
          )}
        >
          <TabButton appearance={unselectedStyle} data-hen-id={triggerIdentity}>
            {tab.label}
          </TabButton>
        </ShadcnTabsTrigger>
      );
    })}
  </ShadcnTabsList>
);

export const Tabs = React.forwardRef<
  React.ElementRef<typeof ShadcnTabs>,
  TabsProps
>((props, ref) => {
  const look = resolveTabsLook(props.variant);
  const {
    className,
    defaultValue: defaultValueProp,
    value: valueProp,
    onValueChange,
    orientation,
    listAlign = 'center',
    unselectedStyle = 'outline',
    color,
    gap,
    panelHeight: panelHeightProp,
    variant: _variant,
    tabs: _tabs,
    children: childrenProp,
    ...rootProps
  } = props as TabsRenderableProps;
  const panelHeight = resolveTabsPanelHeight(panelHeightProp);

  const childRecords = collectTabItems(childrenProp);
  const usingItemsApi =
    childRecords.length === 0 && hasNonEmptyTabsArray(props);
  const records = usingItemsApi ? props.tabs : childRecords;
  const firstValue = records[0]?.value;
  const triggerIdentity = usingItemsApi
    ? COMPONENT_IDS.Tabs
    : COMPONENT_IDS.TabItem;

  const isControlled = valueProp !== undefined;
  // Inside a popup, the Open modal action can name the tab to show, so each
  // button on the page can open its own tab.
  const openPayload = useModalOpenPayload();
  const requestedTab = readRequestedTab(openPayload, records);
  const [uncontrolledValue, setUncontrolledValue] = React.useState(
    () => requestedTab ?? defaultValueProp ?? firstValue,
  );
  // Each open hands over a new payload, so reopening while mounted switches.
  // Tabs can arrive after the payload, so the requested tab is applied once
  // it exists rather than only on mount.
  const [appliedPayload, setAppliedPayload] = React.useState(() =>
    requestedTab === undefined ? undefined : openPayload,
  );
  if (requestedTab !== undefined && appliedPayload !== openPayload) {
    setAppliedPayload(openPayload);
    if (!isControlled) {
      setUncontrolledValue(requestedTab);
    }
  }
  const selectedValue = isControlled
    ? valueProp
    : (uncontrolledValue ?? firstValue);

  const handleValueChange = React.useCallback(
    (next: string) => {
      if (!isControlled) {
        setUncontrolledValue(next);
      }
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  return (
    <ShadcnTabs
      ref={ref}
      className={cn(
        resolveTabsGapClass(gap),
        panelHeight === 'fill' && 'h-full min-h-0',
        listAlign === 'start' &&
          look !== 'track' &&
          'data-[orientation=horizontal]:items-start',
        className,
      )}
      orientation={orientation && look !== 'track' ? orientation : 'horizontal'}
      data-hen-id={COMPONENT_IDS.Tabs}
      {...rootProps}
      {...(selectedValue != null ? { value: selectedValue } : {})}
      onValueChange={handleValueChange}
    >
      {look === 'track' ? (
        <TrackTabTriggers
          records={records}
          color={color}
          selectedValue={selectedValue}
          triggerIdentity={triggerIdentity}
        />
      ) : (
        <TabTriggers
          records={records}
          look={look}
          unselectedStyle={unselectedStyle}
          triggerIdentity={triggerIdentity}
        />
      )}
      {/* hen-ui-allow-raw-html -- stacks force-mounted panels for crossfade / height modes; not a Flex/Grid authoring surface */}
      <div className={tabsPanelContainerClass(panelHeight)}>
        {records.map((tab) => {
          const isActive = tab.value === selectedValue;
          return (
            <ShadcnTabsContent
              key={tab.value}
              value={tab.value}
              forceMount
              className={tabsPanelContentClass(panelHeight, isActive)}
              // Keep forceMount for crossfade + panel state, but remove
              // inactive content from sequential focus and AT navigation.
              // Same inert + aria-hidden pattern as SubNav stacked panels.
              // A truthy string keeps the attribute in both React 18 (unknown
              // attribute) and React 19 (boolean attribute). An empty string
              // is falsy, so React 19 removes it.
              // Also force tabIndex=-1 so force-mounted panels (Radix keeps
              // tabindex=0) leave the sequential order even in environments
              // that do not honor inert for focus.
              aria-hidden={!isActive}
              tabIndex={isActive ? 0 : -1}
              {...(!isActive ? { inert: 'true' as unknown as boolean } : {})}
            >
              {tab.content}
            </ShadcnTabsContent>
          );
        })}
      </div>
    </ShadcnTabs>
  );
});

Tabs.displayName = 'Tabs';

/**
 * The popup's `tab` open value, when it names one of these tabs. Another Tabs
 * in the same popup ignores an ID it does not have.
 */
const readRequestedTab = (
  payload: ModalOpenPayload | undefined,
  records: readonly TabRecord[],
): string | undefined => {
  const raw = payload?.tab;
  if (typeof raw !== 'string') return undefined;
  const tab = raw.trim();
  return records.some((record) => record.value === tab) ? tab : undefined;
};
